import { translate } from '../i18n'
import { formatDateTimeLocalValue, parseDateTimeLocalValue } from '../utils/time'
import {
   formatDurationSeconds,
   formatQuantityInput,
   parseDurationSeconds,
   parseQuantity,
} from '../utils/units'
import { createEditModalState } from './editModalState'
import {
   deleteMeasurement,
   getEntryTimestampMs,
   measurementEntries,
   updateMeasurement,
} from './measurementsStore'
import { unitSystem } from './unitSystemStore'

const measurementEdit = createEditModalState([
   'distance',
   'duration',
   'timestamp',
] as const)
let editingEntryId: string | null = null

export const isMeasurementEditModalOpen = measurementEdit.isOpen
export const editingMeasurementDistance = measurementEdit.valueAccessor('distance')
export const editingMeasurementDuration = measurementEdit.valueAccessor('duration')
export const editingMeasurementTimestamp = measurementEdit.valueAccessor('timestamp')
export const measurementEditError = measurementEdit.error
export const setEditingMeasurementDistance = (value: string) =>
   measurementEdit.setValue('distance', value)
export const setEditingMeasurementDuration = (value: string) =>
   measurementEdit.setValue('duration', value)
export const setEditingMeasurementTimestamp = (value: string) =>
   measurementEdit.setValue('timestamp', value)

/**
 * Opens the edit modal for an entry, pre-filling inputs in the active unit system.
 *
 * @param entryId - The entry to edit.
 */
export function openMeasurementEditModal(entryId: string) {
   const entry = measurementEntries().find(item => item.entryId === entryId)
   if (!entry || entry.deletedAt !== null) {
      return
   }
   editingEntryId = entryId
   measurementEdit.open(0, {
      distance: formatQuantityInput(
         'distance',
         entry.values.distance ?? null,
         unitSystem(),
         2,
      ),
      duration: formatDurationSeconds(entry.values.duration ?? 0),
      timestamp: formatDateTimeLocalValue(getEntryTimestampMs(entry)),
   })
}

/**
 * Closes the edit modal and clears its inputs.
 */
export function closeMeasurementEditModal() {
   editingEntryId = null
   measurementEdit.close()
}

/**
 * Deletes the exercise entry open in the edit modal and closes the modal.
 */
export function deleteEditedMeasurement() {
   const entryId = editingEntryId
   closeMeasurementEditModal()
   if (entryId !== null) {
      deleteMeasurement(entryId)
   }
}

/**
 * Validates and saves the edited entry (values and timestamp).
 *
 * @returns True when saved, false on validation failure.
 */
export function saveEditedMeasurement() {
   if (!editingEntryId) {
      return false
   }
   const distance = parseQuantity('distance', editingMeasurementDistance(), unitSystem())
   const duration = parseDurationSeconds(editingMeasurementDuration())
   if (distance === null || duration === null) {
      measurementEdit.setError(translate('Enter a distance and a duration.'))
      return false
   }
   const timestampMs = parseDateTimeLocalValue(editingMeasurementTimestamp())
   if (timestampMs === null) {
      measurementEdit.setError(translate('Enter a valid date and time.'))
      return false
   }
   const error = updateMeasurement(editingEntryId, {
      values: { distance, duration },
      timestampMs,
   })
   if (error) {
      measurementEdit.setError(error)
      return false
   }
   closeMeasurementEditModal()
   return true
}
